<template>
  <b-sidebar
    :id="id"
    ref="sidebar"
    :visible="isSidebarActive"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >

    <template #default="{ hide }">
      <b-overlay
        v-if="isSidebarActive"
        :show="isLoading"
        rounded="sm"
        variant="secondary"
        opacity="0.75"
      >
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5
            v-t="`modules.team_members.pages.add`"
            class="mb-0"
          />

          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >
          <!-- Body -->
          <b-form
            class="p-2"
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <div>
              <i-input
                id="firstName"
                v-model="itemData.first_name"
                :errors="validationErrors.first_name"
                type="text"
                title="inputs.first_name"
                required
              />
            </div>

            <div>
              <i-input
                id="lastName"
                v-model="itemData.last_name"
                :errors="validationErrors.last_name"
                type="text"
                title="inputs.last_name"
                required
              />
            </div>

            <div>
              <i-input
                id="email"
                v-model="itemData.email"
                :errors="validationErrors.email"
                type="email"
                title="inputs.email"
                required
              />
            </div>

            <div>
              <i-input
                id="mobile"
                v-model="itemData.mobile"
                :errors="validationErrors.mobile"
                type="tel"
                title="inputs.mobile"
                :dial-code="itemData.mobile_code"
                @update:dial-code="val => itemData.mobile_code = val"
              />
            </div>

            <div>
              <i-input
                id="password"
                v-model="itemData.password"
                :errors="validationErrors.password"
                type="password"
                title="inputs.password"
                required
              />
            </div>

            <div>
              <i-input
                id="passwordConfirmation"
                v-model="itemData.password_confirmation"
                :errors="validationErrors.password_confirmation"
                type="password"
                title="inputs.password_confirmation"
                required
              />
            </div>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-t="`Add`"
                variant="primary"
                class="mr-2"
                type="submit"
              />
              <b-button
                v-t="`Cancel`"
                variant="outline-secondary"
                @click="hide"
              />
            </div>
          </b-form>
        </validation-observer>

      </b-overlay>

    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BButton, BOverlay,
} from 'bootstrap-vue'
import { onMounted, onUnmounted, ref } from 'vue'
import { ValidationObserver } from 'vee-validate'
import store from '@/store'
import teamStoreModule from '@/views/models/team/teamStoreModule'
import useModelUpdate from '@/views/models/common/useModelUpdate'
import formValidation from '@core/comp-functions/forms/form-validation'

export default {
  components: {
    ValidationObserver,
    BSidebar,
    BForm,
    BButton,
    BOverlay,
  },
  emits: ['refetch-data'],
  model: {
    prop: 'isSidebarActive',
    event: 'update:is-sidebar-active',
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    id: {
      type: String,
      default: 'sidebar-add-new-team-member',
      required: false,
    },
    itemId: {
      type: String,
      default: null,
    },
    value: {
      type: Object,
      required: false,
      default: null,
    },
  },
  mounted() {
    this.refs.value = this.$refs
  },
  setup(props, ctx) {
    const STORE_MODULE_NAME = 'team_members'

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, teamStoreModule)
    onMounted(() => {
      if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, teamStoreModule)
    })

    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const refs = ref({})
    const emptyItemData = {
      id: null,
      first_name: null,
      last_name: null,
      email: null,
      mobile_code: null,
      mobile: null,
      password: null,
      password_confirmation: null,
    }
    const itemData = ref({})
    const isLoading = ref(false)

    const resetItemData = () => {
      itemData.value = JSON.parse(JSON.stringify(emptyItemData))
      isLoading.value = false
    }

    resetItemData()

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetItemData)

    const { validationErrors, save } = useModelUpdate(
      STORE_MODULE_NAME,
      refs,
      itemData,
      () => {
        ctx.emit('refetch-data')
        ctx.emit('update:is-sidebar-active', false)
      },
      null,
      null,
      () => {
        resetItemData()
        ctx.emit('update:is-sidebar-active', false)
      },
    )

    const onSubmit = () => {
      isLoading.value = true
      save(true,
        () => {
          isLoading.value = false
        }, () => {
          isLoading.value = false
        }, () => {
          isLoading.value = false
        })
    }

    return {
      itemData,
      validationErrors,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetItemData,
      resetForm,

      refs,
      isLoading,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.vti__dropdown-list {
  z-index: 2;
}
</style>
